import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FaCheckCircle, FaExclamationTriangle, FaSpinner, FaEnvelope, FaArrowLeft } from 'react-icons/fa';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(''); // 'success', 'error', 'loading'
  const [message, setMessage] = useState('');
  const [resendLoading, setResendLoading] = useState(false);

  const token = searchParams.get('token');
  const registered = localStorage.getItem('pendingVerificationRegistered') === 'true';
  const email = localStorage.getItem('pendingVerificationEmail');
  const verifyOrigin = localStorage.getItem('verifyOrigin'); // 'login' | 'register' | undefined

  useEffect(() => {
    const success = searchParams.get('success');
    const error = searchParams.get('error');

    if (success) {
      setLoading(false);
      setStatus('success');
      switch (success) {
        case 'verified':
          setMessage('Your email address has been successfully verified! You can now use all features.');
          break;
        case 'already_verified':
          setMessage('Your email address is already verified! You can now use all features.');
          break;
        default:
          setMessage('Email verification completed successfully!');
      }
      // Clear pending verification data from localStorage
      localStorage.removeItem('pendingVerificationEmail');
      localStorage.removeItem('pendingVerificationRegistered');
    } else if (error) {
      setLoading(false);
      setStatus('error');
      switch (error) {
        case 'token_required':
          setMessage('Verification token is required.');
          break;
        case 'invalid_token':
          setMessage('Invalid or expired verification token.');
          break;
        case 'expired_token':
          setMessage('Verification token has expired. Please request a new verification email.');
          break;
        case 'server_error':
          setMessage('An error occurred during verification. Please try again.');
          break;
        default:
          setMessage('Email verification failed.');
      }
    } else if (token) {
      verifyEmail();
    } else if (email) {
      // User needs to verify email (from register or login)
      setStatus('info');
      const emailText = ` for <strong>${email}</strong>`;
      setMessage(`Please check your email inbox${emailText} for the verification link. Click the link to verify your email address.`);
      setLoading(false);
    } else {
      setStatus('error');
      setMessage('Verification token not found.');
      setLoading(false);
    }
  }, [token, email, searchParams]);

  const verifyEmail = async () => {
    try {
      const response = await fetch(`/api/auth/api/verify-email?token=${token}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok) {
        setStatus('success');
        if (data.message === 'Email is already verified') {
          setMessage('Your email address is already verified! You can now use all features.');
        } else {
          setMessage('Your email address has been successfully verified! You can now use all features.');
        }
        // Clear pending verification data from localStorage
        localStorage.removeItem('pendingVerificationEmail');
        localStorage.removeItem('pendingVerificationRegistered');
      } else {
        setStatus('error');
        setMessage(data.message || 'Email verification failed.');
      }
    } catch (error) {
      console.error('Email verification error:', error);
      setStatus('error');
      setMessage('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    try {
      const authToken = localStorage.getItem('token');
      
      // If we have a token, use the normal resend endpoint
      if (authToken) {
        const response = await fetch('/api/auth/verify-email/resend', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        });

        const data = await response.json();

        if (response.ok) {
          setMessage('Verification email resent successfully. Please check your email inbox.');
          setStatus('success');
        } else {
          setMessage(data.message || 'Failed to send verification email.');
          setStatus('error');
        }
      } else if (token) {
        // If no auth but we have verification token, resend using token
        const response = await fetch('/api/auth/verify-email/resend', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ token })
        });

        const data = await response.json();

        if (response.ok) {
          setMessage('Verification email resent successfully. Please check your email inbox.');
          setStatus('success');
        } else {
          setMessage(data.message || 'Failed to send verification email.');
          setStatus('error');
        }
      } else if (email) {
        // Fallback: resend by email if available
        const response = await fetch('/api/auth/verify-email/resend', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ email })
        });

        const data = await response.json();

        if (response.ok) {
          setMessage('Verification email resent successfully. Please check your email inbox.');
          setStatus('success');
        } else {
          setMessage(data.message || 'Failed to send verification email.');
          setStatus('error');
        }
      } else {
        setMessage('Unable to resend verification email. Please try logging in again.');
        setStatus('error');
      }
    } catch (error) {
      console.error('Resend verification error:', error);
      setMessage('An error occurred. Please try again.');
      setStatus('error');
    } finally {
      setResendLoading(false);
    }
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="bg-gray-800 rounded-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <FaSpinner className="animate-spin text-4xl text-blue-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Verifying Email</h2>
            <p className="text-gray-300">Please wait...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center">
      <div className="bg-gray-800 rounded-lg p-8 max-w-md w-full mx-4">
        <div className="text-center">
          {status === 'success' ? (
            <>
              <FaCheckCircle className="text-4xl text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Email Verified!</h2>
              <p className="text-gray-300 mb-6">{message}</p>
              <div className="space-y-3">
                <button
                  onClick={handleGoToDashboard}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors"
                >
                  Go to Dashboard
                </button>
                <button
                  onClick={handleGoToLogin}
                  className="w-full bg-gray-600 hover:bg-gray-700 text-white font-medium py-3 px-4 rounded-lg transition-colors"
                >
                  Login
                </button>
              </div>
            </>
          ) : status === 'info' ? (
            <>
              <FaEnvelope className="text-4xl text-blue-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Check Your Email</h2>
              <p className="text-gray-300 mb-6" dangerouslySetInnerHTML={{ __html: message }}></p>
              <div className="space-y-3">
                {/* Login/Register akışından gelindiyse yeniden gönderme butonu gösterme */}
                {verifyOrigin === null && (
                  <button
                    onClick={handleResendVerification}
                    disabled={resendLoading}
                    className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
                  >
                    {resendLoading ? <FaSpinner className="animate-spin" /> : <FaEnvelope />}
                    <span>{resendLoading ? 'Sending...' : 'Resend Verification Email'}</span>
                  </button>
                )}
                <button
                  onClick={handleGoToLogin}
                  className="w-full bg-gray-600 hover:bg-gray-700 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
                >
                  <FaArrowLeft />
                  <span>Back to Login</span>
                </button>
              </div>
            </>
          ) : (
            <>
              <FaExclamationTriangle className="text-4xl text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Verification Failed</h2>
              <p className="text-gray-300 mb-6">{message}</p>
              <div className="space-y-3">
                {/* Sadece email linkinden gelinip doğrulama başarısızsa yeniden gönderme */}
                {token && (
                  <button
                    onClick={handleResendVerification}
                    disabled={resendLoading}
                    className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
                  >
                    {resendLoading ? <FaSpinner className="animate-spin" /> : <FaEnvelope />}
                    <span>{resendLoading ? 'Sending...' : 'Resend Verification Email'}</span>
                  </button>
                )}
                <button
                  onClick={handleGoToLogin}
                  className="w-full bg-gray-600 hover:bg-gray-700 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
                >
                  <FaArrowLeft />
                  <span>Back to Login</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
